// Utils
import { component } from 'picoapp';

import choozy from 'utils/choozy';
import { hide, parseJSONScript, show } from 'utils/node';
import createFormState from 'utils/product-form-state';
import getString from 'utils/string';
import { dispatch } from 'utils/theme';

export default component((node, ctx) => {
  const { jsonScript, variantSelect } = choozy(node);

  // Properties
  const {
    formId,
    productId,
    variantSelected,
    isMainProduct,
    isMainForm,
    syncOtherForms,
    oneSize,
    receiveSync,
    placement,
  } = node.dataset;

  // Data
  const { products, variantsStock, fitting } = parseJSONScript(jsonScript);

  // State
  let currentProductId = parseInt(productId, 10);

  let previousProductId = null;

  const handleChange = () => {
    const { forms } = ctx.getState();

    ctx.hydrate({
      forms: {
        ...forms,
        [currentProductId]: createFormState(
          products[currentProductId],
          node,
          forms[previousProductId],
        ),
      },
    });

    [
      `product:form:update`,
      `${productId}:${formId}:update`,
      ...(syncOtherForms === 'true' ? [`${productId}:form:sync`] : []),
    ].forEach((event) => {
      document.dispatchEvent(
        new CustomEvent(event, {
          cancelable: true,
          detail: {
            productId,
            formId,
            currentProductId,
          },
        }),
      );
    });
  };

  const handleSubmit = () => {
    const { forms, variantsInventory, variantsPrices } = ctx.getState();
    const { selectedVariant } = forms[parseInt(productId, 10)] || {};

    // Add this product' variant stock to the app state so the cart can use it
    if (selectedVariant) {
      ctx.hydrate({
        variantsInventory: {
          ...variantsInventory,
          [selectedVariant.id]: variantsStock[selectedVariant.id],
        },
        variantsPrices: {
          ...variantsPrices,
          [selectedVariant.id]: selectedVariant.compare_at_price || 0,
        },
      });
    }

    const customSubmitEvent = new CustomEvent(
      selectedVariant ? 'product:form:submit' : 'product:form:submit-error',
      {
        bubbles: true,
        cancelable: false,
        detail: selectedVariant
          ? {
              formId,
              form: node,
              formData: new FormData(node),
              product: products[+productId],
              variant: selectedVariant,
              placement,
              skipCartDrawer: node.dataset.skipCartDrawer === 'true',
            }
          : {
              type: 'NO_VARIANT_SELECTED',
            },
      },
    );

    setTimeout(() => {
      const isSuitSelectorForm = placement === 'suit_selector_form';

      if (isSuitSelectorForm) {
        // document.dispatchEvent is used to trigger adding to cart through the suit selector but without tracking.
        document.dispatchEvent(
          new CustomEvent(selectedVariant ? 'product:form:submit' : 'product:form:submit-error', {
            bubbles: true,
            cancelable: false,
            detail: {
              ...customSubmitEvent.detail,
              skipTracking: true,
            },
          }),
        );
      } else {
        // node.dispatchEvent is used to trigger adding to cart normally.
        node.dispatchEvent(customSubmitEvent);
      }
    }, 1);
  };

  const handleVariantChange = (e) => {
    e.stopPropagation();

    handleChange();
  };

  const handleSizeChange = (e) => {
    e.stopPropagation();

    handleChange();
  };

  const handleFitChange = (e) => {
    e.stopPropagation();

    const formData = new FormData(node);

    previousProductId = currentProductId;
    currentProductId = parseInt(formData.get('product'), 10);

    handleChange();
  };

  const handleUpdate = (e) => {
    const props = e.detail;
    const { forms } = ctx.getState();

    const { selectedVariant, fieldsets } = forms[currentProductId];

    // Update selection labels connected to this form
    const selectedValueLabels = [
      ...document.querySelectorAll(`[data-selected-value-label-form-id='${formId}']`),
    ];

    selectedValueLabels.forEach((element) => {
      const labelText = element.dataset.selectedValueLabel
        .split(',')
        .map((option) => fieldsets.find((fieldset) => fieldset.name === option))
        .filter((fieldset) => fieldset !== undefined && fieldset.selectedOption > -1)
        .map((fieldset) => fieldset.options[fieldset.selectedOption].value)
        .join(' / ');

      if (labelText !== '') {
        element.textContent = labelText;
      } else if (element.dataset.selectedValueLabel === 'product') {
        element.textContent = fitting[currentProductId];
      }
    });

    if (!selectedVariant) return;

    if (
      +variantSelect.value !== +selectedVariant.id &&
      +currentProductId === +window.theme.productId &&
      node.closest('[data-is-main-form="true"]:not([id*="sticky-footer"])') &&
      props.action !== 'init'
    )
      dispatch('pdp-select-size', {
        size: fieldsets
          .filter((fieldset) => fieldset.name !== 'Fit' && fieldset.name !== 'Color')
          .map((fieldset) => fieldset.options[fieldset.selectedOption].value)
          .join(' / '),
      });

    if (variantSelect) {
      variantSelect.value = selectedVariant.id;
      variantSelect.dispatchEvent(new Event('change'));
    }

    if (props.formId === formId) {
      const selectVariantEvent = new CustomEvent('product:form:variant-selected', {
        bubbles: true,
        cancelable: true,
        detail: {
          variant: selectedVariant,
        },
      });

      node.dispatchEvent(selectVariantEvent);
    }

    const variantStockLabels = [
      ...document.querySelectorAll(`[data-stock-quantity-label='${formId}']`),
    ];

    const stock = variantsStock[selectedVariant.id];
    variantStockLabels.forEach((element) => {
      if (stock <= 5 && stock > 0) {
        element.textContent = getString('product.form.stock_quantity', {
          quantity: stock,
        });
        show(element);
      } else {
        hide(element);
      }
    });
  };

  const handleSync = (e) => {
    const props = e.detail;

    if (receiveSync === 'false') return;
    if (props.formId === formId || props.productId !== productId) return;
    if (props.currentProductId !== currentProductId && isMainForm === 'true') return;
    if (props.currentProductId !== currentProductId) currentProductId = props.currentProductId;

    document.dispatchEvent(
      new CustomEvent(`${productId}:${formId}:update`, {
        cancelable: true,
        bubbles: true,
        detail: { currentProductId },
      }),
    );
  };

  // App Events
  node.addEventListener(`${productId}:${formId}:variant-change`, handleVariantChange);
  node.addEventListener(`${productId}:${formId}:size-change`, handleSizeChange);
  node.addEventListener(`${productId}:${formId}:fit-change`, handleFitChange);

  document.addEventListener(`${productId}:${formId}:update`, handleUpdate);
  document.addEventListener(`${productId}:${formId}:submit`, handleSubmit);
  document.addEventListener(`${productId}:form:sync`, handleSync);

  // DOM Events
  node.addEventListener('submit', (e) => {
    e.preventDefault();
    // handleChange(); // TODO: this caused error when submitting before selecting size. but does omitting it cause problems elsewhere?

    setTimeout(() => {
      handleSubmit();
    }, 1);
  });

  node.addEventListener('form:change', (e) => {
    e.preventDefault();
    handleChange();
  });

  // Init
  const init = () => {
    const { forms } = ctx.getState();

    // This will only be true if a form is rendered after initial page load
    if (forms[currentProductId]) {
      document.dispatchEvent(
        new CustomEvent(`${productId}:${formId}:update`, {
          bubbles: false,
          cancelable: true,
          detail: {
            formId,
            productId,
            currentProductId,
            action: 'init',
          },
        }),
      );
    } else if ((variantSelected === 'true' && isMainProduct === 'true') || oneSize === 'true') {
      handleChange(ctx.getState());
    }
  };

  init();

  document.addEventListener(
    `${productId}:${formId}:unmount`,
    () => {
      node.removeEventListener(`${productId}:${formId}:variant-change`, handleVariantChange);
      node.removeEventListener(`${productId}:${formId}:size-change`, handleSizeChange);
      node.removeEventListener(`${productId}:${formId}:fit-change`, handleFitChange);

      document.removeEventListener(`${productId}:${formId}:update`, handleUpdate);
      document.removeEventListener(`${productId}:form:sync`, handleSync);
      document.removeEventListener(`${productId}:${formId}:submit`, handleSubmit);
    },
    { once: true },
  );
});
